/**
 * O que falta no Caso, provado pelo dado (`TP-058`). Cada item diz COMO se prova (`prova`) e em que pé está (`estado`).
 * Nunca o valor do dado, que fica na aba "Informações do caso", salvo as sugestões do item clínico (`TP-060`).
 */

import { emFrase } from '../documentos/emFrase';

/**
 * Como o item se prova. `pessoa`, `sem_fonte` e `sugerido` se fecham pela caixinha do passo; o resto, pelo dado.
 * `sugerido` é o clínico extraído (`TP-060`): aparece como sugestão, mas só uma pessoa o confere.
 */
export type TipoDeProva = 'documento' | 'dado' | 'vinculo' | 'pessoa' | 'sem_fonte' | 'sugerido';

/** O que o e-mail ou um documento validado diz do item clínico. `valor` nulo: diagnóstico, só onde é citado. */
export interface SugestaoDoItem {
  readonly valor: string | null;
  /** O tipo do documento ("Formulário de avaliação") ou "e-mail"; nunca o arquivo. */
  readonly documento: string;
  /** O dado quando ele é uma parte do item ("Oxigenoterapia" em Terapias); `null` quando é o item inteiro. */
  readonly rotulo: string | null;
  /** O campo do e-mail de origem quando o item espera a confirmação do valor (`a_confirmar`, `TP-072`). */
  readonly campoId?: string | null;
  readonly ticketId?: string | null;
}

/**
 * A camada do checklist (`TP-059`): agrupa, não trava. `cadastro` é exigido sempre; `processo_inicial` são os documentos
 * montados depois que a operadora libera a 1ª diária, e ficam fora da conta.
 */
export type Camada = 'cadastro' | 'captacao' | 'porta_1' | 'porta_2' | 'processo_inicial';

/** `falta`, `fabricado` e `diverge` faltam; o resto não. */
export type EstadoDoItem = 'provado' | 'falta' | 'fabricado' | 'diverge' | 'declarado' | 'aceito_sem_prova' | 'dispensado';

export interface OndeOItemE {
  /** `null` = o cadastro do paciente. */
  readonly frente: string | null;
  readonly passo: number | null;
  readonly rotulo: string;
}

export interface ItemDaCompletude {
  readonly chave: string;
  readonly rotulo: string;
  readonly nivel: number;
  readonly prova: TipoDeProva;
  readonly estado: EstadoDoItem;
  readonly pendente: boolean;
  readonly motivo: string | null;
  readonly fontes: readonly string[];
  readonly onde: readonly OndeOItemE[];
  /** As chaves com que a curadoria o exige: a caixinha do passo marca por elas. */
  readonly chavesDaCuradoria: readonly string[];
  /** É o próprio passo por fazer, e não um item dele. */
  readonly passo: boolean;
  readonly caixinhaEm: Date | null;
  /** Quem marcou a caixinha; `null` quando foi o sistema ou não se sabe. */
  readonly caixinhaPor: string | null;
  readonly aceite: { readonly motivo: string; readonly em: Date } | null;
  readonly camada: Camada;
  /** Fora da conta dos níveis: aparece, mas não segura nada. */
  readonly opcional: boolean;
  /** Só em `sugerido`: o que o e-mail e os documentos validados dizem, para a pessoa conferir. */
  readonly sugestoes: readonly SugestaoDoItem[];
  /** Só no item que falta: a última vez que ele passou a faltar (`TP-061`). Nulo no item de passo futuro. */
  readonly faltaDesde: Date | null;
}

export interface FrenteDaCompletude {
  readonly frente: string;
  readonly rotulo: string;
  readonly estado: string;
  readonly passoDaVez: number;
  readonly totalDePassos: number;
  readonly itensFaltando: number;
  readonly passosPorFazer: number;
}

export interface Completude {
  readonly nivelUmCompleto: boolean;
  readonly nivelDoisCompleto: boolean;
  readonly faltam: number;
  /** O corte dos níveis é provisório até a Anery confirmar (NR-150). */
  readonly nivelProvisorio: boolean;
  readonly itens: readonly ItemDaCompletude[];
  readonly frentes: readonly FrenteDaCompletude[];
  /** A versão do checklist do Caso. `configurado` falso: a operadora não tem checklist curado e vale o padrão. */
  readonly checklist: { readonly versao: number | null; readonly configurado: boolean };
}

export const ROTULO_DO_ESTADO: Record<EstadoDoItem, string> = {
  provado: 'Provado',
  falta: 'Falta',
  fabricado: 'Parece inventado',
  diverge: 'As fontes discordam',
  declarado: 'Conferido por uma pessoa',
  aceito_sem_prova: 'Aceito sem prova',
  dispensado: 'Dispensado',
};

export const ROTULO_DA_PROVA: Record<TipoDeProva, string> = {
  documento: 'pelo documento',
  dado: 'pelo dado',
  vinculo: 'pelo IW',
  pessoa: 'por uma pessoa',
  sem_fonte: 'por uma pessoa (o sistema não recebe esse dado)',
  sugerido: 'por uma pessoa (o sistema sugere o que leu)',
};

export const ROTULO_DA_FONTE: Record<string, string> = {
  email: 'e-mail',
  documento: 'documento validado',
  iw: 'IW',
  caso: 'nome com que o caso nasceu',
  pessoa: 'pessoa',
};

/** O motivo na língua da operação. Código desconhecido passa como veio. */
export const TEXTO_DO_MOTIVO: Record<string, string> = {
  sem_valor: 'nenhuma fonte traz o dado',
  fontes_discordam: 'as fontes dizem coisas diferentes (veja em Informações do caso)',
  cpf_tamanho: 'CPF sem 11 dígitos',
  cpf_repetido: 'CPF com todos os dígitos iguais',
  cpf_sequencia: 'CPF em sequência (cadastro temporário)',
  cpf_digito_invalido: 'dígito verificador do CPF não confere',
  nome_uma_palavra: 'nome com uma palavra só (cadastro temporário)',
  nome_abreviado: 'nome abreviado (cadastro temporário)',
  nascimento_sentinela: 'data de nascimento de preenchimento',
  telefone_repetido: 'telefone com todos os dígitos iguais',
  carteira_repetida: 'carteira com todos os dígitos iguais',
  endereco_nao_informado: 'endereço não informado',
  sem_documento: 'documento não enviado',
  falta_validar: 'documento chegou e falta validar',
  documento_diverge: 'o documento que chegou diverge',
  documento_dispensado: 'documento dispensado',
  sem_vinculo: 'paciente sem vínculo no IW',
  preenchido_no_iw: 'preenchido ao criar o paciente no IW',
  nome_do_iw_diverge: 'o nome no IW não bate com o dos documentos',
  por_conferir: 'falta conferir',
  pre_preenchido: 'veio do e-mail ou do documento, falta conferir',
  sem_fonte: 'falta conferir (o sistema não recebe esse dado)',
  por_fazer: 'por fazer',
  frente_dispensada: 'frente dispensada',
  a_confirmar: 'o e-mail traz, falta confirmar',
};

export function textoDoMotivo(motivo: string | null): string | null {
  return motivo === null ? null : (TEXTO_DO_MOTIVO[motivo] ?? motivo);
}

/** O item que se prova pelo dado, documento ou vínculo: a caixinha não o fecha, e ele pode ser aceito sem prova. */
export function provavel(item: ItemDaCompletude): boolean {
  return item.prova === 'documento' || item.prova === 'dado' || item.prova === 'vinculo';
}

/** O item que pode ser aceito sem prova: o provável e o clínico sugerido (o CID que não veio, por exemplo). */
export function aceitavel(item: ItemDaCompletude): boolean {
  return provavel(item) || item.prova === 'sugerido';
}

/** Há quanto tempo o item falta (`TP-061`): "falta desde hoje", "falta há 1 dia", "falta há 3 dias". */
export function textoDeFaltaDesde(desde: Date, agora: Date = new Date()): string {
  const dias = Math.floor((agora.getTime() - desde.getTime()) / 86_400_000);
  if (dias < 1) return 'falta desde hoje';
  return dias === 1 ? 'falta há 1 dia' : `falta há ${String(dias)} dias`;
}

/** A sugestão como a tela a diz: "3x/semana · e-mail", ou "citado no Formulário de avaliação" quando é diagnóstico. */
export function textoDaSugestao(s: SugestaoDoItem): string {
  if (s.valor === null) return `citado no ${s.documento}`;
  const rotulo = s.rotulo === null ? '' : `${emFrase(s.rotulo)}: `;
  return `${rotulo}${s.valor} · ${s.documento}`;
}

/** O id do cartão da frente no painel das frentes, para o clique em "onde" levar a pessoa até onde se age. */
export function idDaFrente(token: string): string {
  return `frente-${token}`;
}
